/**
 * Comitium Releases — runtime: búsqueda, filtrado y timeline.
 * Opera sobre HTML pre-renderizado (sin data.js).
 */

/* ── Módulos ────────────────────────────────────────────────── */
const MODULES = {
  'cms':           { label: 'CMS',           full: 'Comitium CMS',           hue: '#009BCA', tint: 'rgba(0,155,202,0.10)',  ink: '#005f7d' },
  'visual-editor': { label: 'Visual Editor', full: 'Comitium Visual Editor', hue: '#4F46E5', tint: 'rgba(79,70,229,0.08)',  ink: '#3730a3' },
  'ai':            { label: 'AI',            full: 'Comitium AI',            hue: '#8F009B', tint: 'rgba(143,0,155,0.10)',  ink: '#5a0061' },
  'analytics':     { label: 'Analytics',     full: 'Comitium Analytics',     hue: '#A3002D', tint: 'rgba(163,0,45,0.10)',   ink: '#7a0022' },
  'advertising':   { label: 'Advertising',   full: 'Comitium Advertising',   hue: '#7A8500', tint: 'rgba(189,205,5,0.18)',  ink: '#5d6300' },
};
const ALL_MODULES   = Object.keys(MODULES);

/* ── Estado ─────────────────────────────────────────────────── */
const state = {
  query:         '',
  activeModules: new Set(ALL_MODULES),
};

/* ── Helpers ────────────────────────────────────────────────── */

function escAttr(str) {
  return String(str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

// Resalta `q` dentro de `html` sin tocar las tags/atributos (evita romper <a href="…">, title="…", etc.):
// solo envuelve las coincidencias que caen en nodos de texto reales.
function highlight(html, q) {
  if (!html) return '';
  if (!q)    return html;

  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex   = new RegExp(`(${escaped})`, 'gi');

  const container = document.createElement('div');
  container.innerHTML = html;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  const textNodes = [];
  let node;
  while ((node = walker.nextNode())) {
    regex.lastIndex = 0;
    if (regex.test(node.nodeValue)) textNodes.push(node);
  }

  textNodes.forEach(textNode => {
    const frag = document.createDocumentFragment();
    regex.lastIndex = 0;
    textNode.nodeValue.split(regex).forEach((part, i) => {
      if (i % 2 === 1) {
        const mark = document.createElement('mark');
        mark.className = 'hl';
        mark.textContent = part;
        frag.appendChild(mark);
      } else if (part) {
        frag.appendChild(document.createTextNode(part));
      }
    });
    textNode.parentNode.replaceChild(frag, textNode);
  });

  return container.innerHTML;
}

// Comprueba si `query` coincide con algún token numérico exacto de `versionStr`.
// "7.26 / 8.12" → tokens ["7.26","8.12"] → versionTokenMatch(..., "7.26") = true
// "7.26.0 / 8.12.0" → tokens ["7.26.0","8.12.0"] → versionTokenMatch(..., "7.26") = false
function versionTokenMatch(versionStr, query) {
  const tokens = String(versionStr || '').toLowerCase().split(/[\s\/]+/).filter(t => /\d/.test(t));
  return tokens.some(t => t === query);
}

/* ── Render: chips de módulo ────────────────────────────────── */

function renderFilterChips() {
  const container = document.getElementById('module-chips');
  if (!container) return;
  container.innerHTML = Object.entries(MODULES).map(([key, m]) => {
    const on = state.activeModules.has(key);
    return `<button
      class="c-filter-chip${on ? ' c-filter-chip--active' : ''}"
      style="${on ? `background:${m.hue};border-color:${m.hue};` : ''}"
      data-module="${key}"
      aria-pressed="${on}"
      title="${m.full}">Comitium ${m.label}</button>`;
  }).join('');
}

/* ── Stats desde DOM ────────────────────────────────────────── */

function renderStats() {
  const all    = Array.from(document.querySelectorAll('#releases-container .c-release'));
  const lastCMS = all.find(r => !r.dataset.track);

  const statVersion = document.getElementById('stat-version');
  const statCount   = document.getElementById('stat-count');
  const statChanges = document.getElementById('stat-changes');

  if (statVersion) statVersion.textContent = lastCMS ? lastCMS.dataset.version : '—';
  if (statCount)   statCount.textContent   = all.length;
  if (statChanges) statChanges.textContent = document.querySelectorAll('#releases-container .c-item-row').length;
}

/* ── Timeline desde DOM ─────────────────────────────────────── */

function updateTimelineDots() {
  const timeline = document.getElementById('floating-timeline');
  if (!timeline) return;

  const releases = Array.from(document.querySelectorAll('#releases-container .c-release'));
  timeline.innerHTML = releases.slice(0, 20).map(rel => {
    const slug    = rel.id;
    const modKey  = rel.dataset.dominantModule || 'core';
    const m       = MODULES[modKey];
    const tooltip = `${escAttr(rel.dataset.version || '')} — ${escAttr(rel.dataset.headline || '')}`;

    return `<a href="#${slug}" class="c-timeline__dot" title="${tooltip}" style="background:${m.hue};" data-slug="${slug}"></a>`;
  }).join('\n');

  timeline.querySelectorAll('.c-timeline__dot').forEach(dot => {
    dot.addEventListener('click', e => {
      e.preventDefault();
      const el = document.getElementById(dot.dataset.slug);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); history.pushState(null, '', '#' + dot.dataset.slug); }
    });
  });
}

/* ── Filtrado DOM ───────────────────────────────────────────── */

function render() {
  const q         = state.query.trim().toLowerCase();
  const container = document.getElementById('releases-container');
  const emptyEl   = document.getElementById('releases-empty');
  let visibleCount = 0;

  // ¿La búsqueda parece un número de versión? (ej. "7.26", "7.26.0", "3.4")
  const isVersionQ = /^\d+(\.\d+)+$/.test(q);

  container.querySelectorAll('.c-release').forEach(article => {
    let relMatch        = false;
    let patchSpecific   = false;
    let matchingPatches = null; // elementos .c-patch-group que coinciden

    if (q) {
      if (isVersionQ) {
        // 1) ¿Coincide con la versión minor de la release? ("7.26", "8.12", "3.4"…)
        relMatch = versionTokenMatch(article.dataset.version, q);

        if (!relMatch) {
          // 2) ¿Coincide con un patch concreto? ("7.26.0", "8.12.1", "3.4.0"…)
          const allGroups = Array.from(article.querySelectorAll('[data-patch]'));
          matchingPatches = allGroups.filter(g => versionTokenMatch(g.dataset.patch, q));
          if (matchingPatches.length > 0) {
            relMatch      = true;
            patchSpecific = true;
          }
        }
      } else {
        // Búsqueda de texto: incluye headline + versiones para "surface whole family"
        relMatch = (article.dataset.versionIds || '').toLowerCase().includes(q);
      }
    }

    let improvedTotal = 0, fixedTotal = 0, articleVisible = false;

    article.querySelectorAll('[data-col-block]').forEach(colBlock => {
      const kind = colBlock.dataset.kind;
      let colTotal = 0;

      colBlock.querySelectorAll('[data-patch]').forEach(patchGroup => {
        // En modo patch-específico, ocultar los grupos que no coinciden
        const skipPatch = patchSpecific && !matchingPatches.includes(patchGroup);
        let groupCount = 0;

        patchGroup.querySelectorAll('.c-item-row').forEach(item => {
          const passes = state.activeModules.has(item.dataset.module);
          let textMatch;

          if (skipPatch) {
            textMatch = false;
          } else if (!q || relMatch) {
            textMatch = true;
          } else {
            textMatch = ((item.dataset.title || '') + ' ' + (item.dataset.desc || '')).toLowerCase().includes(q);
          }

          const visible = passes && textMatch;
          item.style.display = visible ? '' : 'none';

          if (visible) {
            // Resaltar solo en búsquedas de texto (no de versión)
            const hl      = (!isVersionQ && q && !relMatch) ? q : '';
            const titleEl = item.querySelector('[data-item-title]');
            const descEl  = item.querySelector('[data-item-desc]');
            if (titleEl && item.dataset.title) titleEl.innerHTML = highlight(item.dataset.title, hl);
            if (descEl  && item.dataset.desc)  descEl.innerHTML  = highlight(item.dataset.desc,  hl);
          }
          if (visible) groupCount++;
        });

        patchGroup.style.display = groupCount > 0 ? '' : 'none';
        const divCount = patchGroup.querySelector('[data-patch-count]');
        if (divCount) divCount.textContent = groupCount;
        colTotal += groupCount;
      });

      const colCountEl = colBlock.querySelector('[data-col-count]');
      if (colCountEl) colCountEl.textContent = colTotal;

      const colEmptyEl = colBlock.querySelector('[data-col-empty]');
      if (colEmptyEl) colEmptyEl.style.display = colTotal === 0 ? '' : 'none';

      if (kind === 'improved') improvedTotal = colTotal;
      else if (kind === 'fixed') fixedTotal = colTotal;
      if (colTotal > 0) articleVisible = true;
    });

    const iEl = article.querySelector('.c-release__improved-count');
    const fEl = article.querySelector('.c-release__fixed-count');
    if (iEl) iEl.textContent = improvedTotal;
    if (fEl) fEl.textContent = fixedTotal;

    article.style.display = articleVisible ? '' : 'none';
    if (articleVisible) visibleCount++;
  });

  if (visibleCount === 0) {
    emptyEl.hidden = false;
    const q = state.query.trim();
    emptyEl.querySelector('[data-empty-query]').textContent = q;
    emptyEl.querySelector('[data-empty-para]').hidden = !q;
  } else {
    emptyEl.hidden = true;
  }

  updateTimelineDots();
}

/* ── Scroll: dot activo ─────────────────────────────────────── */

let _scrollTimer = null;
function onScroll() {
  clearTimeout(_scrollTimer);
  _scrollTimer = setTimeout(() => {
    const spreads   = document.querySelectorAll('.c-release');
    const threshold = 130;
    let active      = null;
    spreads.forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.top <= threshold && r.bottom > 0) active = el;
    });
    if (!active && spreads.length) active = spreads[0];
    document.querySelectorAll('.c-timeline__dot').forEach(dot => {
      dot.classList.toggle('c-timeline__dot--active', active && dot.dataset.slug === active.id);
    });
  }, 50);
}

/* ── Eventos ────────────────────────────────────────────────── */

let _searchTimer = null;
function onSearchInput(e) {
  clearTimeout(_searchTimer);
  _searchTimer = setTimeout(() => { state.query = e.target.value; render(); }, 180);
}

function onModuleChipClick(e) {
  const btn = e.target.closest('.c-filter-chip');
  if (!btn || !btn.dataset.module) return;
  const mod = btn.dataset.module;
  if (state.activeModules.has(mod)) {
    state.activeModules.delete(mod);
    if (state.activeModules.size === 0) ALL_MODULES.forEach(k => state.activeModules.add(k));
  } else {
    state.activeModules.add(mod);
  }
  renderFilterChips();
  render();
}

function scrollToRelease(slug, e) {
  if (e) e.preventDefault();
  const el = document.getElementById(slug);
  if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); history.pushState(null, '', '#' + slug); }
}

function copyPermalink(slug, e, triggerEl) {
  if (e) e.preventDefault();
  const url = location.origin + location.pathname + '#' + slug;
  navigator.clipboard?.writeText(url).then(() => {
    const tip = triggerEl?.querySelector('[data-tooltip-text]');
    if (!tip) return;
    const copiedLabel = tip.dataset.labelCopied || tip.textContent;
    const defaultLabel = tip.dataset.labelCopy || tip.textContent;
    tip.textContent = copiedLabel;
    tip.classList.add('c-tooltip--visible');
    setTimeout(() => {
      tip.textContent = defaultLabel;
      tip.classList.remove('c-tooltip--visible');
    }, 2000);
  });
}

// El bundle de webpack envuelve este script en su propio scope: los atributos
// onclick="…" del HTML se ejecutan en el scope global, así que hay que colgar
// aquí explícitamente las funciones que el twig invoca inline, o quedan "undefined".
window.scrollToRelease = scrollToRelease;
window.copyPermalink   = copyPermalink;

function scrollToHash() {
  const hash = window.location.hash.slice(1);
  if (!hash) return;
  requestAnimationFrame(() => {
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

/* ── Init ───────────────────────────────────────────────────── */

document.addEventListener('DOMContentLoaded', () => {
  renderStats();
  renderFilterChips();

  const searchInput   = document.getElementById('search-input');
  const chipsContainer = document.getElementById('module-chips');
  if (searchInput)    searchInput.addEventListener('input', onSearchInput);
  if (chipsContainer) chipsContainer.addEventListener('click', onModuleChipClick);

  updateTimelineDots();

  window.addEventListener('scroll', onScroll, { passive: true });

  if (window.location.hash) scrollToHash();
});
